import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Rocket, Trash2, Edit, Code, Megaphone, Calendar, FolderOpen, Clock, ListTodo, ChevronDown, ChevronUp, Lock, Sparkles, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradeButton } from '@/components/FeatureGate';

const CATEGORY_CONFIG = {
  software: { label: 'Software', icon: Code, color: 'bg-blue-500' },
  marketing: { label: 'Marketing', icon: Megaphone, color: 'bg-purple-500' },
  event: { label: 'Event', icon: Calendar, color: 'bg-orange-500' },
  custom: { label: 'Eigene', icon: FolderOpen, color: 'bg-gray-500' },
  other: { label: 'Andere', icon: FileText, color: 'bg-slate-500' },
};

const Templates = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { canUseAIFeatures } = useSubscription();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiGeneratedTemplate, setAiGeneratedTemplate] = useState(null);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_duration_days: 30,
    category: 'custom',
    default_tasks: []
  });
  const [useFormData, setUseFormData] = useState({
    project_name: '',
    start_date: new Date().toISOString().split('T')[0],
    planned_cost: ''
  });
  const [newTask, setNewTask] = useState({ title: '', description: '', duration_days: 7, priority: 'medium' });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/templates`, formData);
      toast.success('Vorlage erfolgreich erstellt!');
      setCreateDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Vorlage');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    try {
      await axios.put(`${API}/templates/${selectedTemplate.id}`, formData);
      toast.success('Vorlage aktualisiert!');
      setEditDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Vorlage "${template.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/templates/${template.id}`);
      toast.success('Vorlage gelöscht!');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // AI Template Generation
  const handleGenerateAI = async () => {
    if (!aiDescription.trim()) {
      toast.error('Bitte beschreiben Sie Ihr Projekt');
      return;
    }
    
    setAiGenerating(true);
    try {
      const response = await axios.post(`${API}/templates/generate-ai`, {
        description: aiDescription
      });
      setAiGeneratedTemplate(response.data);
      toast.success('Vorlage wurde generiert!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der KI-Generierung');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSaveAITemplate = async () => {
    if (!aiGeneratedTemplate) return;
    
    try {
      await axios.post(`${API}/templates`, {
        name: aiGeneratedTemplate.name,
        description: aiGeneratedTemplate.description,
        category: aiGeneratedTemplate.category,
        default_duration_days: aiGeneratedTemplate.default_duration_days,
        default_tasks: aiGeneratedTemplate.default_tasks
      });
      toast.success('Vorlage gespeichert!');
      setAiDialogOpen(false);
      setAiDescription('');
      setAiGeneratedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleUseTemplate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.post(
        `${API}/projects/from-template/${selectedTemplate.id}?project_name=${encodeURIComponent(useFormData.project_name)}&start_date=${useFormData.start_date}&planned_cost=${useFormData.planned_cost || 0}`
      );
      toast.success('Projekt aus Vorlage erstellt!');
      setUseDialogOpen(false);
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Projekts');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_duration_days: 30,
      category: 'custom',
      default_tasks: []
    });
    setNewTask({ title: '', description: '', duration_days: 7, priority: 'medium' });
    setSelectedTemplate(null);
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      default_duration_days: template.default_duration_days,
      category: template.category || 'custom',
      default_tasks: template.default_tasks || []
    });
    setEditDialogOpen(true);
  };

  const openUseDialog = (template) => {
    setSelectedTemplate(template);
    setUseFormData({
      project_name: '',
      start_date: new Date().toISOString().split('T')[0],
      planned_cost: ''
    });
    setUseDialogOpen(true);
  };

  const addTask = () => {
    if (!newTask.title.trim()) return;
    setFormData({
      ...formData,
      default_tasks: [...formData.default_tasks, { ...newTask, order: formData.default_tasks.length }]
    });
    setNewTask({ title: '', description: '', duration_days: 7, priority: 'medium' });
  };

  const removeTask = (index) => {
    setFormData({
      ...formData,
      default_tasks: formData.default_tasks.filter((_, i) => i !== index)
    });
  };

  const getCategoryInfo = (category) => CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;

  // Group templates by category
  const systemTemplates = templates.filter(t => t.is_system);
  const userTemplates = templates.filter(t => !t.is_system);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-medium">Laden...</div>
        </div>
      </Layout>
    );
  }

  const TaskForm = () => (
    <div className="space-y-4 border-t pt-4 mt-4">
      <Label>Tasks hinzufügen</Label>
      <div className="flex gap-2">
        <Input
          placeholder="Task-Titel"
          value={newTask.title}
          onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
          className="flex-1"
        />
        <Input
          type="number"
          placeholder="Tage"
          value={newTask.duration_days}
          onChange={(e) => setNewTask({ ...newTask, duration_days: parseInt(e.target.value) || 1 })}
          className="w-20"
        />
        <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Niedrig</SelectItem>
            <SelectItem value="medium">Mittel</SelectItem>
            <SelectItem value="high">Hoch</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" onClick={addTask} size="icon" variant="outline">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      {formData.default_tasks.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {formData.default_tasks.map((task, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">{task.title}</span>
                <Badge variant="outline" className="text-xs">{task.duration_days}d</Badge>
                <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'} className="text-xs">
                  {task.priority}
                </Badge>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => removeTask(i)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const TemplateCard = ({ template }) => {
    const categoryInfo = getCategoryInfo(template.category);
    const CategoryIcon = categoryInfo.icon;
    const isExpanded = expandedTemplate === template.id;

    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded ${categoryInfo.color} text-white`}>
                <CategoryIcon className="w-4 h-4" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {template.name}
                  {template.is_system && (
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      System
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  {categoryInfo.label} • {template.default_duration_days} Tage
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-1">
              {!template.is_system && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(template)} title="Bearbeiten">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template)} title="Löschen">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{template.description}</p>
          
          {template.default_tasks?.length > 0 && (
            <div className="mb-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-between text-xs"
                onClick={() => setExpandedTemplate(isExpanded ? null : template.id)}
              >
                <span className="flex items-center gap-1">
                  <ListTodo className="w-3 h-3" />
                  {template.default_tasks.length} Tasks
                </span>
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </Button>
              
              {isExpanded && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {template.default_tasks.map((task, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
                      <span>{task.title}</span>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {task.duration_days}d
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <Button className="w-full" onClick={() => openUseDialog(template)} data-testid={`use-template-${template.id}`}>
            <Rocket className="w-4 h-4 mr-2" />
            Projekt erstellen
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Projektvorlagen
            </h1>
            <p className="text-muted-foreground mt-2">Erstellen Sie wiederverwendbare Projektvorlagen mit vordefinierten Tasks</p>
          </div>
          <div className="flex gap-2">
            {/* AI Generate Button - Feature Gated */}
            {canUseAIFeatures() ? (
              <Dialog open={aiDialogOpen} onOpenChange={(open) => {
                setAiDialogOpen(open);
                if (!open) {
                  setAiDescription('');
                  setAiGeneratedTemplate(null);
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="ai-template-button">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Mit KI erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      KI-Vorlage generieren
                    </DialogTitle>
                    <DialogDescription>Beschreiben Sie Ihr Projekt und die KI erstellt automatisch eine passende Vorlage mit Tasks</DialogDescription>
                  </DialogHeader>
                
                {!aiGeneratedTemplate ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Projektbeschreibung</Label>
                      <Textarea
                        value={aiDescription}
                        onChange={(e) => setAiDescription(e.target.value)}
                        placeholder="z.B. Eine Webseite für ein Restaurant mit Online-Reservierung, Menü-Anzeige und Kontaktformular..."
                        rows={4}
                      />
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                      <p className="font-medium mb-1">Tipps für bessere Ergebnisse:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Beschreiben Sie das Projektziel</li>
                        <li>Nennen Sie wichtige Features oder Meilensteine</li>
                        <li>Geben Sie den Umfang oder die Komplexität an</li>
                      </ul>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setAiDialogOpen(false)}>Abbrechen</Button>
                      <Button onClick={handleGenerateAI} disabled={aiGenerating || !aiDescription.trim()}>
                        {aiGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generiere...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generieren
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-lg">{aiGeneratedTemplate.name}</h3>
                        <Badge>{CATEGORY_CONFIG[aiGeneratedTemplate.category]?.label || 'Andere'}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{aiGeneratedTemplate.description}</p>
                      <p className="text-xs text-muted-foreground">Dauer: {aiGeneratedTemplate.default_duration_days} Tage • {aiGeneratedTemplate.default_tasks.length} Tasks</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Generierte Tasks</Label>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {aiGeneratedTemplate.default_tasks.map((task, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground text-xs">{i + 1}.</span>
                              <span>{task.title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{task.duration_days}d</Badge>
                              <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'} className="text-xs">
                                {task.priority}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setAiGeneratedTemplate(null)}>
                        Neu generieren
                      </Button>
                      <Button onClick={handleSaveAITemplate}>
                        <Plus className="w-4 h-4 mr-2" />
                        Als Vorlage speichern
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            ) : (
              <UpgradeButton
                feature="ai_features"
                variant="outline"
                data-testid="ai-template-button-locked"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Mit KI erstellen
              </UpgradeButton>
            )}
            
            {/* Manual Create Button */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-template-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Vorlage
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Neue Vorlage erstellen</DialogTitle>
                  <DialogDescription>Erstellen Sie eine wiederverwendbare Projektvorlage mit Tasks</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Vorlagenname</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kategorie</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="software">Software</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="event">Event</SelectItem>
                          <SelectItem value="custom">Eigene</SelectItem>
                          <SelectItem value="other">Andere</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Beschreibung</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standard-Projektdauer (Tage)</Label>
                    <Input
                      type="number"
                      value={formData.default_duration_days}
                      onChange={(e) => setFormData({ ...formData, default_duration_days: parseInt(e.target.value) || 30 })}
                      min="1"
                    />
                  </div>
                  <TaskForm />
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button type="submit">Vorlage erstellen</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* System Templates */}
        {systemTemplates.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-muted-foreground" />
              System-Vorlagen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {systemTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </div>
        )}

        {/* User Templates */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Eigene Vorlagen</h2>
          {userTemplates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Keine eigenen Vorlagen</h3>
                <p className="text-muted-foreground mb-4">Erstellen Sie Ihre erste Vorlage oder nutzen Sie eine System-Vorlage</p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Vorlage
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vorlage bearbeiten</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateTemplate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vorlagenname</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kategorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="custom">Eigene</SelectItem>
                      <SelectItem value="other">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Standard-Projektdauer (Tage)</Label>
                <Input
                  type="number"
                  value={formData.default_duration_days}
                  onChange={(e) => setFormData({ ...formData, default_duration_days: parseInt(e.target.value) || 30 })}
                  min="1"
                />
              </div>
              <TaskForm />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
                <Button type="submit">Speichern</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Use Template Dialog */}
        <Dialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Projekt aus Vorlage erstellen</DialogTitle>
              <DialogDescription>
                {selectedTemplate && (
                  <span>Vorlage: <strong>{selectedTemplate.name}</strong> ({selectedTemplate.default_tasks?.length || 0} Tasks)</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUseTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label>Projektname</Label>
                <Input
                  value={useFormData.project_name}
                  onChange={(e) => setUseFormData({ ...useFormData, project_name: e.target.value })}
                  placeholder="Mein neues Projekt"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Startdatum</Label>
                <Input
                  type="date"
                  value={useFormData.start_date}
                  onChange={(e) => setUseFormData({ ...useFormData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Geplante Kosten (CHF)</Label>
                <Input
                  type="number"
                  value={useFormData.planned_cost}
                  onChange={(e) => setUseFormData({ ...useFormData, planned_cost: e.target.value })}
                  placeholder="0"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUseDialogOpen(false)}>Abbrechen</Button>
                <Button type="submit">
                  <Rocket className="w-4 h-4 mr-2" />
                  Projekt erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Templates;
